import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { X, LogOut, User, Shield, Menu, Building2, Package, ChevronDown, ExternalLink, Copy, AlertCircle, Settings, ShoppingCart, Sparkles, MessageSquare, Palette, Check, ChevronRight, ChevronLeft, Globe, BarChart3, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@mui/material';

import { useAuth } from '@/app/providers';
import { toast } from 'react-hot-toast';
import { LanguageSwitcher } from '@/shared/ui';
import { useTranslations } from '@/shared/hooks';
import { useOrgaConfigurationContext } from '@/app/providers';
import OrganizationSwitcher from '@/components/admin/OrganizationSwitcher';
import { buildUrlWithOrg } from '@/shared/lib/brandUtils';
import AuthModal from '@/components/auth/AuthModal';
import { useTheme } from '@/shared/contexts/ThemeContext';
import { useOrderPersonalization } from '@/shared/hooks/useOrderPersonalization';
import { isLiveAssistantEnabled, isSavChatEnabled } from '@/shared/config/featureFlags';
import { NotificationBell } from '@/features/notifications/components/NotificationBell';
import { ShippingCreditsIndicator } from '@/features/admin/components/ShippingCreditsIndicator';

const Header: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, logout, isAdminForOrganization, affiliations } = useAuth();
  const { themeName, setTheme, availableThemes } = useTheme();
  const { changeLanguage, getLanguages, i18n } = useTranslations();

  // Extraire l'organizationId directement de l'URL au lieu d'utiliser useNavigation
  // car useParams ne fonctionne pas correctement dans Header
  const organizationId = React.useMemo(() => {
    const pathSegments = location.pathname.split('/');
    const orgaIndex = pathSegments.indexOf('orga');
    if (orgaIndex !== -1 && pathSegments[orgaIndex + 1]) {
      return pathSegments[orgaIndex + 1];
    }
    return undefined;
  }, [location.pathname]);

  // Statut global du portail (mêmes conditions que dans l'état de configuration)
  // Utilisation de useOrgaConfigurationContext qui est maintenant accessible ici grâce au hook centralisé
  const { completionStatus } = useOrgaConfigurationContext();
  const {
    isGeneralReady,
    isStripeValid: isStripeReady,
    hasShippingMethods: hasShippingConfigured,
    hasSenderAddress,
    hasContactConfigured,
    isPortalReady
  } = completionStatus;

  // Récupérer les styles de personnalisation pour les boutons (si disponible)
  let personalization;
  try {
    personalization = useOrderPersonalization();
  } catch (e) {
    // Si useOrderPersonalization n'est pas disponible (pas dans PersonalizationProvider), utiliser des valeurs par défaut
    personalization = {
      button: {
        primary: '#2563EB',
        primaryText: '#FFFFFF',
        primaryHover: '#1D4ED8',
        borderRadius: 'rounded-lg',
      },
    };
  }

  // Helper pour obtenir les styles des boutons
  const getButtonStyles = () => {
    const style: React.CSSProperties = {};
    const classes: string[] = ['px-4 py-2 font-medium transition-colors'];

    if (personalization?.button?.primary) {
      if (personalization.button.primary.includes('gradient') || personalization.button.primary.includes('linear-gradient')) {
        style.background = personalization.button.primary;
      } else {
        style.backgroundColor = personalization.button.primary;
      }
    }
    if (personalization?.button?.primaryText) {
      style.color = personalization.button.primaryText;
    }
    if (personalization?.button?.borderRadius) {
      classes.push(personalization.button.borderRadius);
    } else {
      classes.push('rounded-lg');
    }

    return { style, className: classes.join(' ') };
  };

  const getButtonHoverStyles = (): React.CSSProperties => {
    const style: React.CSSProperties = {};
    if (personalization?.button?.primaryHover) {
      if (personalization.button.primaryHover.includes('gradient') || personalization.button.primaryHover.includes('linear-gradient')) {
        style.background = personalization.button.primaryHover;
        style.backgroundColor = '';
      } else {
        style.backgroundColor = personalization.button.primaryHover;
        style.background = '';
      }
    }
    return style;
  };

  // Debug: afficher les valeurs dans la console pour comprendre pourquoi le lien ne s'affiche pas
  const isLandingPage = location.pathname === '/' || location.pathname === '/auth/login' || location.pathname === '/auth/register';

  const hasOwnedOrganization = Array.isArray(affiliations) && affiliations.some((affiliation) => affiliation.role === 'owner');

  // Détecter si on est dans une route avec idorga (format: /orga/[idorga]/...)
  const orgUrlRegex = /^\/orga\/[^/]+/;
  const hasOrganizationInUrl = orgUrlRegex.exec(location.pathname) !== null;

  // Helpers styles pro pour les liens de navigation
  const baseLinkClass = "inline-flex items-center h-9 px-3 rounded-md text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-slate-600";
  const mkNeutral = (active: boolean) => `${baseLinkClass} ${active ? 'bg-slate-800 text-white shadow-lg border border-slate-600 scale-105' : 'text-slate-200 hover:text-white hover:bg-slate-800 hover:scale-105'}`;

  // Chemins utiles + détection actif
  const orgBase = organizationId ? `/orga/${organizationId}` : '';
  const pathAdmin = `${orgBase}/admin`;
  const isActivePath = (p: string) => location.pathname === p || location.pathname.startsWith(`${p}/`);

  // Vérifier les permissions pour l'organisation actuelle
  const isAdminForCurrentOrg = organizationId ? isAdminForOrganization(organizationId) : false;

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isRegisterMode, setIsRegisterMode] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const [copiedPortalLink, setCopiedPortalLink] = useState(false);

  const [menuView, setMenuView] = useState<'main' | 'theme' | 'language'>('main');

  // Reset menu view when dropdown closes
  useEffect(() => {
    if (!showUserDropdown) {
      setTimeout(() => setMenuView('main'), 200);
    }
  }, [showUserDropdown]);

  const handleLogout = async () => {
    try {
      await logout();
      toast.success(t('common.success'));
      navigate('/');
    } catch (error_) {
      console.error('Logout error:', error_);
      toast.error(t('common.error'));
    }
  };

  const closeMobileMenu = () => setShowMobileMenu(false);

  // Lien de portail commandes à partager (visible pour les pages d'orga)
  const portalPath = hasOrganizationInUrl ? buildUrlWithOrg('/order', organizationId) : '';
  const portalUrl = typeof window !== 'undefined' && portalPath
    ? `${window.location.origin}${portalPath}`
    : '';

  const handleCopyPortalLink = async () => {
    if (!portalUrl) return;
    try {
      await navigator.clipboard.writeText(portalUrl);
      setCopiedPortalLink(true);
      toast.success(t('admin.configuration.copied'));
      setTimeout(() => setCopiedPortalLink(false), 2000);
    } catch (error_) {
      console.error('Copy portal link error:', error_);
      toast.error(t('common.error'));
    }
  };

  // Gestion du clic en dehors du dropdown utilisateur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
      <nav className="fixed top-0 z-50 w-full bg-slate-900 border-b border-slate-800">
        <div className="px-3 py-3 lg:px-5 lg:pl-3">
          <div className="flex items-center justify-between">
            {/* Left side - Navigation */}
            <div className="flex items-center justify-start rtl:justify-end">
              {/* Mobile menu button */}
              <button
                data-drawer-target="logo-sidebar"
                data-drawer-toggle="logo-sidebar"
                aria-controls="logo-sidebar"
                type="button"
                className="inline-flex items-center p-2 text-sm text-gray-500 rounded-lg md:hidden hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:text-gray-400 dark:hover:bg-gray-700 dark:focus:ring-gray-600"
                onClick={() => setShowMobileMenu(true)}
              >
                <span className="sr-only">{t('header.openMenu')}</span>
                <Menu className="w-6 h-6" />
              </button>

              {/* OrganizationSwitcher - Affiché tout à gauche quand on est dans une organisation (caché sur mobile) */}
              {!isLandingPage && currentUser && hasOwnedOrganization && hasOrganizationInUrl && (
                <div className="hidden md:flex items-center ml-2">
                  <OrganizationSwitcher />
                </div>
              )}

              {/* Logo organisation (ou défaut) - Affiché seulement si pas dans une organisation */}
              {!hasOrganizationInUrl && (
                <Link to="/" className="flex ms-2 md:me-24 items-center">
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-md flex items-center justify-center shadow-sm">
                    <Building2 className="w-5 h-5 text-white" />
                  </div>
                </Link>
              )}

              {/* Liens Organisations / Mes commandes */}
              {!isLandingPage && currentUser && (
                <div className="hidden md:flex items-center space-x-2 ml-4">
                  {!hasOrganizationInUrl && (
                    <Link
                      to="/organizations/manage"
                      className={`${mkNeutral(location.pathname === '/organizations/manage')} space-x-2`}
                      title={t('header.manageOrganizations')}
                    >
                      <Building2 className="h-4 w-4" />
                      <span>{t('header.organizations')}</span>
                    </Link>
                  )}

                  {!location.pathname.includes('/admin') && (
                    <Link
                      to="/my-orders"
                      className={`${mkNeutral(isActivePath('/my-orders'))} space-x-2`}
                      title={t('header.viewOrders')}
                    >
                      <Package className="h-4 w-4" />
                      <span>{t('header.myOrders')}</span>
                    </Link>
                  )}
                </div>
              )}
            </div>

            {/* Right side - User actions */}
            <div className="flex items-center">
              {/* Shipping Credits Indicator - Visible only for admins in org context, but NOT in order journey */}
              {currentUser && hasOrganizationInUrl && isAdminForCurrentOrg && organizationId && !location.pathname.includes('/order') && (
                <div className="flex mr-3">
                  <ShippingCreditsIndicator organizationId={organizationId} />
                </div>
              )}

              {/* Portal share link ou message de configuration (desktop) */}
              {hasOrganizationInUrl && isAdminForCurrentOrg && !location.pathname.includes('/order') && (
                <div className="hidden md:flex items-center space-x-3 mr-4">
                  {isPortalReady && portalUrl ? (
                    <>
                      <Tooltip
                        title={
                          <div className="text-center py-1">
                            <div className="font-semibold mb-1">Lien du portail de commande</div>
                            <div className="text-xs mb-2">
                              Cliquez pour copier le lien à partager avec vos clients
                            </div>
                            <div className="text-xs font-mono bg-slate-700 px-2 py-1 rounded break-all">
                              {portalUrl}
                            </div>
                          </div>
                        }
                        arrow
                        placement="bottom"
                      >
                        <button
                          type="button"
                          onClick={handleCopyPortalLink}
                          className="flex items-center space-x-2 px-3 py-1.5 bg-slate-800 rounded-md border border-slate-700 hover:border-emerald-500 hover:bg-slate-700 transition-all cursor-pointer"
                        >
                          {copiedPortalLink ? (
                            <>
                              <Copy className="w-3.5 h-3.5 text-emerald-400" />
                              <span className="text-xs font-medium text-emerald-400">Lien copié !</span>
                            </>
                          ) : (
                            <>
                              <ExternalLink className="w-3.5 h-3.5 text-emerald-400" />
                              <span className="text-xs font-medium text-emerald-400">Lien à partager</span>
                            </>
                          )}
                        </button>
                      </Tooltip>
                    </>
                  ) : (
                    <Tooltip
                      title={
                        <div className="py-2 px-1 max-w-sm">
                          <div className="font-semibold mb-2 text-sm">⚠️ {t('admin.configuration.portalDisabled.tooltip.title')}</div>
                          <div className="text-xs space-y-2">
                            <p className="mb-2">
                              {t('admin.configuration.portalDisabled.tooltip.description')}
                            </p>
                            <div className="space-y-1.5 text-left">
                              <div className="flex items-start gap-2">
                                <span>{isGeneralReady ? '✅' : '⭕'}</span>
                                <div>
                                  <strong>{t('admin.configuration.portalDisabled.tooltip.general').split(':')[0]}:</strong>
                                  <span>{t('admin.configuration.portalDisabled.tooltip.general').split(':').slice(1).join(':')}</span>
                                </div>
                              </div>
                              <div className="flex items-start gap-2">
                                <span>{isStripeReady ? '✅' : '⭕'}</span>
                                <div>
                                  <strong>{t('admin.configuration.portalDisabled.tooltip.stripe').split(':')[0]}:</strong>
                                  <span>{t('admin.configuration.portalDisabled.tooltip.stripe').split(':').slice(1).join(':')}</span>
                                </div>
                              </div>
                              <div className="flex items-start gap-2">
                                <span>{hasShippingConfigured ? '✅' : '⭕'}</span>
                                <div>
                                  <strong>{t('admin.configuration.portalDisabled.tooltip.shipping').split(':')[0]}:</strong>
                                  <span>{t('admin.configuration.portalDisabled.tooltip.shipping').split(':').slice(1).join(':')}</span>
                                </div>
                              </div>
                              <div className="flex items-start gap-2">
                                <span>{hasSenderAddress ? '✅' : '⭕'}</span>
                                <div>
                                  <strong>{t('admin.configuration.portalDisabled.tooltip.senderAddress').split(':')[0]}:</strong>
                                  <span>{t('admin.configuration.portalDisabled.tooltip.senderAddress').split(':').slice(1).join(':')}</span>
                                </div>
                              </div>
                              <div className="flex items-start gap-2">
                                <span>{hasContactConfigured ? '✅' : '⭕'}</span>
                                <div>
                                  <strong>{t('admin.configuration.portalDisabled.tooltip.contacts').split(':')[0]}:</strong>
                                  <span>{t('admin.configuration.portalDisabled.tooltip.contacts').split(':').slice(1).join(':')}</span>
                                </div>
                              </div>
                            </div>
                            <p className="mt-2 pt-2 border-t border-gray-600 italic">
                              {t('admin.configuration.portalDisabled.tooltip.clickToConfigure')}
                            </p>
                          </div>
                        </div>
                      }
                      arrow
                      placement="bottom"
                    >
                      <Link
                        to={`/orga/${organizationId}/admin/configuration`}
                        className="flex items-center space-x-2 px-3 py-2 bg-red-900/30 rounded-lg border border-red-600 hover:bg-red-900/40 transition-all cursor-pointer shadow-md hover:shadow-lg"
                      >
                        <AlertCircle className="w-5 h-5 text-red-400 animate-pulse flex-shrink-0" />
                        <div className="flex flex-col">
                          <span className="text-xs font-bold text-red-300 leading-tight">
                            ⚠️ {t('admin.configuration.portalDisabled.title')}
                          </span>
                          <span className="text-[10px] text-red-400 leading-tight mt-0.5">
                            {t('admin.configuration.portalDisabled.subtitle')}
                          </span>
                        </div>
                      </Link>
                    </Tooltip>
                  )}
                </div>
              )}

              {/* Notifications - Visible uniquement si connecté */}
              {currentUser && (
                <div className="mr-4">
                  <NotificationBell />
                </div>
              )}



              {/* Language Switcher - Visible si non connecté */}
              {!currentUser && (
                <div className="mr-4">
                  <LanguageSwitcher showName={false} />
                </div>
              )}

              {/* User pill + dropdown (style compact) */}
              {currentUser ? (
                <div className="relative" ref={userDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setShowUserDropdown((v) => !v)}
                    className="inline-flex items-center gap-3 bg-slate-800 text-slate-200 rounded-xl px-3 py-2 shadow-sm hover:bg-slate-700 transition-colors"
                  >
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-r from-blue-500 to-purple-600">
                      <User className="w-4 h-4 text-white" />
                    </span>
                    <div className="hidden sm:flex flex-col items-start text-left">
                      <span className="text-sm font-semibold leading-4 truncate max-w-[12rem]">
                        {currentUser.displayName || 'Utilisateur'}
                      </span>
                      <span className="text-xs text-slate-400 truncate max-w-[12rem]">
                        {currentUser.email}
                      </span>
                    </div>
                    {isAdminForCurrentOrg && hasOrganizationInUrl && (
                      <span className="hidden sm:inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                        {t('header.admin')}
                      </span>
                    )}
                    <ChevronDown className={`w-4 h-4 transition-transform ${showUserDropdown ? 'rotate-180' : ''}`} />
                  </button>

                  {showUserDropdown && (
                    <div className="absolute right-0 mt-2 w-72 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-2 z-50 overflow-hidden transform transition-all">

                      {/* MAIN VIEW */}
                      {menuView === 'main' && (
                        <>
                          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 mb-1">
                            <p className="text-sm font-semibold text-gray-800 dark:text-white truncate">{currentUser.displayName || 'Utilisateur'}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{currentUser.email}</p>
                          </div>

                          <div className="py-1">
                            {/* Theme Button */}
                            <button
                              onClick={() => setMenuView('theme')}
                              className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                              <div className="flex items-center gap-3">
                                <Palette className="w-4 h-4 text-gray-500" />
                                <span>Thème</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <div
                                  className="w-3 h-3 rounded-full border border-gray-200 dark:border-gray-600"
                                  style={{ backgroundColor: availableThemes.find(t => t.name === themeName)?.previewColor }}
                                />
                                <ChevronRight className="w-4 h-4 text-gray-400" />
                              </div>
                            </button>

                            {/* Language Button */}
                            <button
                              onClick={() => setMenuView('language')}
                              className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                              <div className="flex items-center gap-3">
                                <Globe className="w-4 h-4 text-gray-500" />
                                <span>Langue</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-sm">{getLanguages().find(l => l.code === i18n.language)?.flag}</span>
                                <ChevronRight className="w-4 h-4 text-gray-400" />
                              </div>
                            </button>
                          </div>

                          <div className="border-t border-gray-100 dark:border-gray-700 mt-1 pt-1">
                            <button
                              onClick={() => {
                                setShowUserDropdown(false);
                                handleLogout();
                              }}
                              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors text-left"
                            >
                              <LogOut className="w-4 h-4" />
                              {t('header.logout')}
                            </button>
                          </div>
                        </>
                      )}

                      {/* THEME SUBMENU */}
                      {menuView === 'theme' && (
                        <div className="animate-in slide-in-from-right duration-200">
                          <div className="px-2 py-2 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2 mb-1">
                            <button
                              onClick={() => setMenuView('main')}
                              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                            >
                              <ChevronLeft className="w-5 h-5 text-gray-500" />
                            </button>
                            <span className="font-semibold text-sm">Choisir un thème</span>
                          </div>
                          <div className="max-h-[300px] overflow-y-auto py-1">
                            {availableThemes.map((theme) => {
                              const isActive = theme.name === themeName;
                              return (
                                <button
                                  key={theme.name}
                                  onClick={() => setTheme(theme.name)}
                                  className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors ${isActive
                                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                                    }`}
                                >
                                  <div className="flex items-center gap-3">
                                    <div
                                      className="w-4 h-4 rounded-full border border-gray-200 dark:border-gray-600 shadow-sm"
                                      style={{ backgroundColor: theme.previewColor }}
                                    />
                                    <span className="font-medium">{theme.displayName}</span>
                                  </div>
                                  {isActive && (
                                    <Check className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* LANGUAGE SUBMENU */}
                      {menuView === 'language' && (
                        <div className="animate-in slide-in-from-right duration-200">
                          <div className="px-2 py-2 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2 mb-1">
                            <button
                              onClick={() => setMenuView('main')}
                              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                            >
                              <ChevronLeft className="w-5 h-5 text-gray-500" />
                            </button>
                            <span className="font-semibold text-sm">Choisir une langue</span>
                          </div>
                          <div className="py-1">
                            {getLanguages().map((lang) => {
                              const isActive = lang.code === i18n.language;
                              return (
                                <button
                                  key={lang.code}
                                  onClick={() => changeLanguage(lang.code)}
                                  className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors ${isActive
                                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                                    }`}
                                >
                                  <div className="flex items-center gap-3">
                                    <span className="text-lg leading-none">{lang.flag}</span>
                                    <span className="font-medium">{lang.name}</span>
                                  </div>
                                  {isActive && (
                                    <Check className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setShowLoginModal(true)}
                    className="text-slate-200 hover:text-white font-medium transition-colors"
                  >
                    {t('header.signIn')}
                  </button>
                  <button
                    onClick={() => {
                      setIsRegisterMode(true);
                      setShowLoginModal(true);
                    }}
                    {...getButtonStyles()}
                    onMouseEnter={(e) => {
                      const hoverStyles = getButtonHoverStyles();
                      Object.assign(e.currentTarget.style, hoverStyles);
                    }}
                    onMouseLeave={(e) => {
                      const buttonStyles = getButtonStyles();
                      Object.assign(e.currentTarget.style, buttonStyles.style);
                    }}
                  >
                    {t('header.register')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      {showMobileMenu && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={closeMobileMenu}
          />

          {/* Menu panel */}
          <div className="fixed top-0 left-0 h-full w-80 max-w-[85vw] bg-slate-900 shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-3">
                {/* OrganizationSwitcher - Affiché dans le mobile quand on est dans une organisation */}
                {hasOrganizationInUrl && currentUser && hasOwnedOrganization ? (
                  <OrganizationSwitcher className="w-full" />
                ) : (
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-md flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-white" />
                  </div>
                )}
              </div>
              <button
                onClick={closeMobileMenu}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Mes organisations link - Affiché si connecté ET pas d'idorga dans l'URL */}
              {currentUser && !hasOrganizationInUrl && (
                <Link
                  to="/organizations/manage"
                  onClick={closeMobileMenu}
                  className={`w-full flex items-center font-medium py-3 px-3 rounded-md transition-all duration-200 ${location.pathname === '/organizations/manage'
                    ? 'bg-primary text-white shadow-lg border border-primary-dark scale-105'
                    : 'text-gray-200 hover:bg-slate-800 hover:scale-105'
                    }`}
                >
                  <Building2 className="h-5 w-5 mr-3" />
                  <span>{t('header.organizations')}</span>
                </Link>
              )}

              {/* Mes commandes - Toujours affiché si connecté */}
              {currentUser && (
                <Link
                  to="/my-orders"
                  onClick={closeMobileMenu}
                  className={`w-full flex items-center font-medium py-3 px-3 rounded-md transition-all duration-200 ${location.pathname === '/my-orders'
                    ? 'bg-primary text-white shadow-lg border border-primary-dark scale-105'
                    : 'text-gray-200 hover:bg-slate-800 hover:scale-105'
                    }`}
                >
                  <Package className="h-5 w-5 mr-3" />
                  <span>{t('header.myOrders')}</span>
                </Link>
              )}

              {/* Liens Admin de l'organisation - inclus dans le menu mobile */}
              {hasOrganizationInUrl && currentUser && (
                <div className="space-y-2 pt-2 border-t border-gray-700">
                  {/* Tableau de bord - Seulement si admin */}
                  {isAdminForCurrentOrg && (
                    <Link
                      to={pathAdmin}
                      onClick={closeMobileMenu}
                      className={`w-full flex items-center font-medium py-3 px-3 rounded-md transition-all duration-200 ${isActivePath(pathAdmin)
                        ? 'bg-primary text-white shadow-lg border border-primary-dark'
                        : 'text-gray-200 hover:bg-slate-800 border border-gray-700'
                        }`}
                    >
                      <Shield className="h-5 w-5 mr-3" />
                      <span>{t('header.admin')}</span>
                    </Link>
                  )}

                  {/* Analytics - Seulement si admin */}
                  {isAdminForCurrentOrg && (
                    <Link
                      to={`${orgBase}/admin/analytics`}
                      onClick={closeMobileMenu}
                      className={`w-full flex items-center font-medium py-3 px-3 rounded-md transition-all duration-200 ${location.pathname.includes('/admin/analytics')
                        ? 'bg-primary text-white shadow-lg border border-primary-dark'
                        : 'text-gray-200 hover:bg-slate-800 border border-gray-700'
                        }`}
                    >
                      <BarChart3 className="h-5 w-5 mr-3" />
                      <span>Analytics</span>
                    </Link>
                  )}

                  {/* Clients - Seulement si admin */}
                  {isAdminForCurrentOrg && (
                    <Link
                      to={`${orgBase}/admin/customers`}
                      onClick={closeMobileMenu}
                      className={`w-full flex items-center font-medium py-3 px-3 rounded-md transition-all duration-200 ${location.pathname.includes('/admin/customers')
                        ? 'bg-primary text-white shadow-lg border border-primary-dark'
                        : 'text-gray-200 hover:bg-slate-800 border border-gray-700'
                        }`}
                    >
                      <Users className="h-5 w-5 mr-3" />
                      <span>Clients</span>
                    </Link>
                  )}

                  {/* Assistant IA Live - Seulement si admin ET fonctionnalité activée */}
                  {isAdminForCurrentOrg && isLiveAssistantEnabled(organizationId) && (
                    <Link
                      to={`${orgBase}/admin/live-assistant`}
                      onClick={closeMobileMenu}
                      className={`w-full flex items-center font-medium py-3 px-3 rounded-md transition-all duration-200 ${location.pathname.includes('/admin/live-assistant')
                        ? 'bg-primary text-white shadow-lg border border-primary-dark'
                        : 'text-gray-200 hover:bg-slate-800 border border-gray-700'
                        }`}
                    >
                      <Sparkles className="h-5 w-5 mr-3" />
                      <span>Assistant IA Live</span>
                    </Link>
                  )}

                  {/* Chat SAV - Seulement si admin ET fonctionnalité activée */}
                  {isAdminForCurrentOrg && isSavChatEnabled(organizationId) && (
                    <Link
                      to={`${orgBase}/admin/sav`}
                      onClick={closeMobileMenu}
                      className={`w-full flex items-center font-medium py-3 px-3 rounded-md transition-all duration-200 ${location.pathname.includes('/admin/sav')
                        ? 'bg-primary text-white shadow-lg border border-primary-dark'
                        : 'text-gray-200 hover:bg-slate-800 border border-gray-700'
                        }`}
                    >
                      <MessageSquare className="h-5 w-5 mr-3" />
                      <span>Chat SAV</span>
                    </Link>
                  )}

                  {isAdminForCurrentOrg && (
                    <Link
                      to={`${orgBase}/admin/configuration`}
                      onClick={closeMobileMenu}
                      className={`w-full flex items-center font-medium py-3 px-3 rounded-md transition-all duration-200 ${location.pathname.includes('/admin/configuration')
                        ? 'bg-primary text-white shadow-lg border border-primary-dark'
                        : 'text-gray-200 hover:bg-slate-800 border border-gray-700'
                        }`}
                    >
                      <Settings className="h-5 w-5 mr-3" />
                      <span>{t('admin.configuration.sections.general.title')}</span>
                    </Link>
                  )}

                  <Link
                    to={portalPath}
                    onClick={closeMobileMenu}
                    className={`w-full flex items-center font-medium py-3 px-3 rounded-md transition-all duration-200 text-gray-200 hover:bg-slate-800 border border-transparent`}
                  >
                    <ShoppingCart className="h-5 w-5 mr-3" />
                    <span>{t('admin.configuration.accessOrderForm')}</span>
                  </Link>

                  {/* Portal Status - After all sections (admin only) */}
                  {isAdminForCurrentOrg && !location.pathname.includes('/order') && (
                    <div className="pt-3 mt-3 border-t border-gray-700">
                      {isPortalReady && portalUrl ? (
                        <div className="p-3 bg-green-900/20 border border-green-600/30 rounded-lg space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-green-400">✓ Portail de commande actif</span>
                          </div>
                          <p className="text-[10px] text-green-300/80">Les commandes peuvent être passées</p>
                        </div>
                      ) : (
                        <Link
                          to={`/orga/${organizationId}/admin/configuration`}
                          onClick={closeMobileMenu}
                          className="block p-3 bg-red-900/30 border border-red-600 rounded-lg hover:bg-red-900/40 transition-all"
                        >
                          <div className="flex items-start space-x-2">
                            <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <p className="text-xs font-bold text-red-300 leading-tight">
                                ⚠️ {t('admin.configuration.portalDisabled.title')}
                              </p>
                              <p className="text-[10px] text-red-400 leading-tight mt-1">
                                {t('admin.configuration.portalDisabled.subtitle')}
                              </p>
                            </div>
                          </div>
                        </Link>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Auth buttons - Mobile */}
              {!currentUser && (
                <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => {
                      setShowLoginModal(true);
                      closeMobileMenu();
                    }}
                    className="w-full text-left py-3 px-3 rounded-md font-medium text-slate-200 hover:text-white hover:bg-slate-800 transition-colors"
                  >
                    {t('header.signIn')}
                  </button>
                  <button
                    onClick={() => {
                      setIsRegisterMode(true);
                      setShowLoginModal(true);
                      closeMobileMenu();
                    }}
                    className={`w-full py-3 px-3 font-medium transition-colors ${personalization?.button?.borderRadius && !personalization.button.borderRadius.includes('#') ? personalization.button.borderRadius : 'rounded-md'}`}
                    style={getButtonStyles().style}
                    onMouseEnter={(e) => {
                      const hoverStyles = getButtonHoverStyles();
                      Object.assign(e.currentTarget.style, hoverStyles);
                    }}
                    onMouseLeave={(e) => {
                      const buttonStyles = getButtonStyles();
                      Object.assign(e.currentTarget.style, buttonStyles.style);
                    }}
                  >
                    {t('header.register')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <AuthModal
        isOpen={showLoginModal}
        isRegisterMode={isRegisterMode}
        onClose={() => setShowLoginModal(false)}
        onModeChange={setIsRegisterMode}
      />
    </>
  );
};

export default Header;